import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, BookOpen, FileText, AlertCircle, Sparkles, Clock, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatRelativeTime } from '../lib/fileUtils';

export default function TaxResearch() {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [uploadMode, setUploadMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentQuestion, setDocumentQuestion] = useState('');

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await api.get('/api/tax-research/history');
      setHistory(res.data || []);
    } catch (error) {
      console.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAsk = async () => {
    if (!query.trim()) {
      toast.error('Please enter a question');
      return;
    }

    setLoading(true);
    setResponse(null);
    try {
      const res = await api.post('/api/tax-research/ask', { query, context: {} });
      setResponse(res.data);
      setQuery('');
      loadHistory();
      toast.success('Research complete');
    } catch (error) {
      toast.error('Failed to get research answer');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeDocument = async () => {
    if (!selectedFile || !documentQuestion.trim()) {
      toast.error('Please select a file and enter a question');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('question', documentQuestion);

      const res = await api.post('/api/tax-research/analyze-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Display analysis
      setResponse({
        query: `Document Analysis: ${selectedFile.name} - ${documentQuestion}`,
        short_answer: res.data.analysis.tax_implications,
        detailed_explanation: `Document: ${selectedFile.name}\\n\\nKey Clauses:\\n${res.data.analysis.key_clauses?.join('\\n')}\\n\\nRecommendations:\\n${res.data.analysis.recommendations?.join('\\n')}`,
        structured_output: {
          issue: documentQuestion,
          facts: `Analysis of ${selectedFile.name}`,
          law: res.data.analysis.applicable_law?.join(', '),
          application: res.data.analysis.tax_implications,
          conclusion: res.data.analysis.recommendations?.join(' ')
        },
        sources: [{ type: 'document', reference: selectedFile.name, description: 'Uploaded for analysis' }],
        confidence_score: 80,
        risk_rating: res.data.analysis.risk_rating || 'medium',
        related_topics: [],
        planning_suggestions: res.data.analysis.recommendations || []
      });
      
      setSelectedFile(null);
      setDocumentQuestion('');
      setUploadMode(false);
      loadHistory();
      toast.success('Document analyzed');
    } catch (error) {
      toast.error('Failed to analyze document');
    } finally {
      setLoading(false);
    }
  };

  const handleExportMemo = async () => {
    if (!response) return;
    
    try {
      // Find the query ID from history
      const latestQuery = history[0];
      if (!latestQuery) {
        toast.error('No query to export');
        return;
      }

      const res = await api.post(`/api/tax-research/${latestQuery.id}/export-memo`, {
        format: 'pdf',
        client_friendly: false
      });

      // Download as text file
      const blob = new Blob([res.data.content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Memo exported');
    } catch (error) {
      toast.error('Failed to export memo');
    }
  };

  const getRiskColor = (risk) => {
    if (risk === 'low') return 'bg-green-100 text-green-800';
    if (risk === 'medium') return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-8 h-8 text-foreground" />
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="tax-research-title">
            Tax Research Assistant
          </h1>
        </div>
        <p className="text-muted-foreground mt-1">AI-powered Australian tax research with legislative citations</p>
      </div>

      {/* Query Input */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Ask a Tax Question
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={() => setUploadMode(!uploadMode)}
                variant={uploadMode ? "default" : "outline"}
                size="sm"
                data-testid="toggle-upload-mode"
              >
                <FileText className="w-4 h-4 mr-2" />
                {uploadMode ? 'Text Query' : 'Analyze Document'}
              </Button>
              {response && (
                <Button
                  onClick={handleExportMemo}
                  variant="outline"
                  size="sm"
                  data-testid="export-memo-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Memo
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!uploadMode ? (
            <>
              <Textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g., Can dividends be declared before year-end profit is finalized?\n\nWhat are Division 7A loan repayment rules?\n\nGST treatment for mixed-use rental property?"
                rows={4}
                className="text-base"
                data-testid="research-query-input"
              />
              <div className="flex gap-3">
                <Button 
                  onClick={handleAsk}
                  disabled={loading || !query.trim()}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="ask-button"
                >
                  {loading ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Researching...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Get Answer
                    </>
                  )}
                </Button>
                {query && (
                  <Button onClick={() => setQuery('')} variant="outline">Clear</Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <p className="text-xs text-muted-foreground w-full mb-1">Suggested queries:</p>
                {['Division 7A loan requirements', 'Rental property deductions', 'Minimum wage for casual employee', 'Superannuation guarantee rate'].map((suggestion, i) => (
                  <Button
                    key={i}
                    onClick={() => setQuery(suggestion)}
                    size="sm"
                    variant="outline"
                    className="text-xs"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">Document Analysis</h4>
                <p className="text-sm text-purple-800 mb-4">
                  Upload a tax document (trust deed, loan agreement, financial statement) and ask a specific question about it.
                </p>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Upload Document (PDF, Word, Excel)</Label>
                    <Input
                      type="file"
                      accept=".pdf,.docx,.xlsx,.txt"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      data-testid="document-upload-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Question about this document</Label>
                    <Textarea
                      value={documentQuestion}
                      onChange={(e) => setDocumentQuestion(e.target.value)}
                      placeholder="e.g., Does this trust deed allow dividend distributions?\nWhat are the Division 7A implications of this loan agreement?"
                      rows={3}
                      data-testid="document-question-input"
                    />
                  </div>
                  <Button
                    onClick={handleAnalyzeDocument}
                    disabled={loading || !selectedFile || !documentQuestion.trim()}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="analyze-document-button"
                  >
                    {loading ? 'Analyzing...' : 'Analyze Document'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Response */}
      {response && (
        <Card className="border-l-4 border-l-[#0055FF]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Research Result</CardTitle>
              <div className="flex gap-2">
                <Badge className="bg-blue-100 text-blue-800">
                  Confidence: {response.confidence_score}%
                </Badge>
                <Badge className={getRiskColor(response.risk_rating)}>
                  Risk: {response.risk_rating.toUpperCase()}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="summary" className="w-full">
              <TabsList>
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="detailed">Detailed Analysis</TabsTrigger>
                <TabsTrigger value="structured">Structured Output</TabsTrigger>
                <TabsTrigger value="sources">Sources ({response.sources?.length || 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="summary" className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2">Short Answer</h4>
                  <p className="text-green-800">{response.short_answer}</p>
                </div>
                {response.planning_suggestions?.length > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Tax Planning Suggestions</h4>
                    <ul className="list-disc pl-6 text-sm text-blue-800 space-y-1">
                      {response.planning_suggestions.map((suggestion, i) => (
                        <li key={i}>{suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="detailed">
                <div className="prose prose-slate max-w-none">
                  <p className="text-foreground/90 whitespace-pre-wrap">{response.detailed_explanation}</p>
                </div>
              </TabsContent>

              <TabsContent value="structured">
                <div className="space-y-4">
                  {Object.entries(response.structured_output || {}).map(([key, value]) => (
                    <div key={key} className="p-4 border rounded-lg">
                      <h4 className="font-semibold text-foreground mb-2 capitalize">{key}</h4>
                      <p className="text-foreground/90 text-sm">{value}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="sources">
                <div className="space-y-3">
                  {response.sources?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No sources cited</p>
                  ) : (
                    response.sources.map((source, i) => (
                      <div key={i} className="p-4 border rounded-lg hover:bg-muted/50">
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-foreground flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">{source.type}</Badge>
                              <span className="font-mono font-semibold text-sm text-foreground">{source.reference}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{source.description}</p>
                            {source.url && (
                              <a
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline mt-1 inline-block"
                              >
                                {source.url} →
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Research History */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Research History</CardTitle>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="text-center py-8">
              <Clock className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50 animate-spin" />
              <p className="text-muted-foreground">Loading history...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <p>No research history yet</p>
              <p className="text-sm mt-1">Your tax research queries will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.slice(0, 10).map((item) => (
                <div 
                  key={item.id} 
                  className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer"
                  onClick={() => {
                    setQuery(item.query);
                    const parsed = JSON.parse(item.response);
                    setResponse({
                      query: item.query,
                      short_answer: parsed.short_answer || '',
                      detailed_explanation: parsed.detailed_explanation || '',
                      structured_output: parsed.structured_output || {},
                      sources: item.sources || [],
                      confidence_score: item.confidence_score,
                      risk_rating: item.risk_rating,
                      related_topics: item.related_modules || [],
                      planning_suggestions: parsed.planning_suggestions || []
                    });
                  }}
                  data-testid={`history-item-${item.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{item.query}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-muted-foreground">{item.user_name}</span>
                        <span className="text-xs text-muted-foreground/70">•</span>
                        <span className="text-xs text-muted-foreground">{formatRelativeTime(item.created_at)}</span>
                        {item.related_modules?.length > 0 && (
                          <>
                            <span className="text-xs text-muted-foreground/70">•</span>
                            <Badge variant="outline" className="text-xs">{item.related_modules[0]}</Badge>
                          </>
                        )}
                      </div>
                    </div>
                    <Badge className={getRiskColor(item.risk_rating)}>
                      {item.risk_rating}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}